import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, History, MessageSquare, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useDemoStore } from '@/state/store';
import { can, roleLabel } from '@/domain/roles';
import { fmtNumber } from '@/domain/numbers';
import { formatDateTime, formatRange, formatRangeShort } from '@/domain/time';
import { DEVIATION_STATUS_LABELS } from '@/lib/labels';
import { machineLabel, presentId, presentText } from '@/lib/present';
import { buildSimulationRecord, simulateIdleReduction } from '@/metrics/simulation';
import { EmptyState, PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { DeviationStatusBadge, Note, SeverityBadge } from '@/components/app/Badges';
import { PageHeader, DefList } from '@/components/app/PageHeader';
import { ProvenanceList } from '@/components/app/RecordViewer';
import { cn } from '@/lib/utils';

function parseDeviationId(id) {
  const m = /^([^:]+):(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2}):/.exec(id ?? '');
  if (!m) return null;
  return { scenarioId: m[1], fromDate: m[2], toDate: m[3] };
}

export function SimulationPanel({ result, deviationId, scenario }) {
  const { role, session } = useSession();
  const store = useDemoStore();
  const r = useResults();
  const [pct, setPct] = useState(10);
  const [lph, setLph] = useState('6');
  const panelRef = useRef(null);

  const wantFocus = r.sp.get('simuler') === '1';
  useEffect(() => {
    if (wantFocus && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      r.set({ simuler: null }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantFocus]);

  const available = result.idleHours.status === 'ok' && result.fuelLitres.status === 'ok' && result.tonnes.value > 0;
  const lphNum = Number(String(lph).replace(',', '.'));
  const validLph = Number.isFinite(lphNum) && lphNum >= 0 && lphNum <= 50;
  const input = available ? { baseIdleHours: result.idleHours.value, baseFuelLitres: result.fuelLitres.value, baseTonnes: result.tonnes.value, reductionPct: pct, litresPerIdleHour: validLph ? lphNum : 0, rates: result.rates } : null;
  const out = input ? simulateIdleReduction(input) : null;
  const saved = store.simulations.filter((s) => s.deviationId === deviationId || (!deviationId && !s.deviationId));
  const canSave = can(role, 'save_simulation');

  const save = () => {
    const now = new Date().toISOString();
    const id = `SIM-${scenario.id}-${result.query.fromDate}-${Date.now().toString(36).toUpperCase()}`;
    store.addSimulation(buildSimulationRecord({ id, scenarioId: scenario.id, seedVersion: scenario.seedVersion, deviationId, fromDate: result.query.fromDate, toDate: result.query.toDate, input, output: out, savedAt: now, savedBy: session.demoUser, role }));
    toast.success(`Tiltaksforslag ${id} lagret`, { description: 'Lagret lokalt. Historiske målinger er uendret.' });
  };

  return (
    <Panel ref={panelRef} data-testid="simulation-panel">
      <PanelHeader title="Simuler mindre tomgang" description="Unngått tomgang blir motor av. Produksjon og produktive timer er uendret." />
      <div className="space-y-6 px-5 py-4">
        {!available ? (
          <Note tone="amber" data-testid="simulation-unavailable">Simulering krever komplette tomgangs- og dieseltall og produksjon &gt; 0. {result.idleHours.status !== 'ok' ? 'Tomgangstimer er ufullstendige.' : result.fuelLitres.status !== 'ok' ? 'Dieselforbruket er ufullstendig.' : 'Ingen produksjon i perioden.'}</Note>
        ) : (
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="space-y-5 lg:col-span-5">
              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="sim-pct">Antatt reduksjon i tomgang</Label>
                  <span className="hr-num text-base font-semibold text-[var(--hr-heading)]" data-testid="simulate-idle-pct-value">{pct} %</span>
                </div>
                <Slider id="sim-pct" min={0} max={30} step={1} value={[pct]} onValueChange={(v) => setPct(v[0])} aria-label="Antatt reduksjon i tomgang (0–30 %)" data-testid="simulate-idle-slider" />
                <div className="flex justify-between text-[13px] text-[var(--hr-muted)]"><span>0 %</span><span>30 %</span></div>
                <Input type="number" min={0} max={30} step={1} value={pct} onChange={(e) => setPct(Math.min(30, Math.max(0, Number(e.target.value) || 0)))} aria-label="Reduksjon i prosent" className="hr-tabular h-10 w-28 rounded-[10px] bg-white" data-testid="simulate-idle-pct-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sim-lph">Antatt liter per tomgangstime</Label>
                <Input id="sim-lph" inputMode="decimal" value={lph} onChange={(e) => setLph(e.target.value)} className={cn('hr-tabular h-10 w-28 rounded-[10px] bg-white', !validLph && 'border-[var(--hr-red)]')} data-testid="simulate-idle-lph-input" aria-invalid={!validLph} />
                {!validLph && <p className="text-[13px] text-[var(--hr-red)]">Oppgi et tall mellom 0 og 50.</p>}
                <p className="text-[13px] leading-5 text-[var(--hr-muted)]">Antatt sats.</p>
              </div>
            </div>
            <div className="hr-inset px-5 py-4 lg:col-span-7">
              <div className="mb-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Beregning</div>
              <DefList
                cols={2}
                items={[
                  { label: `Unngått tomgang (${pct} % av ${fmtNumber(input.baseIdleHours, 1)} h)`, value: `${fmtNumber(out.avoidedIdleHours, 2)} h`, testId: 'sim-avoided-hours' },
                  { label: `Illustrativ dieselreduksjon (× ${fmtNumber(lphNum, 1)} L/h)`, value: `${fmtNumber(out.fuelSavedLitres, 1)} L`, testId: 'sim-fuel-saved' },
                  { label: `Mulig drivstoffbesparelse (× ${result.rates.dieselNokPerLitre} NOK/L)`, value: `${fmtNumber(out.nokSaved, 0)} NOK`, testId: 'sim-nok-saved' },
                  { label: `Unngått CO₂ (× ${result.rates.co2KgPerLitre} kg/L)`, value: `${fmtNumber(out.co2SavedKg, 3)} kg`, testId: 'sim-co2-saved' },
                  { label: 'Nytt forbruk', value: `${fmtNumber(out.newFuelLitres, 1)} L (fra ${fmtNumber(input.baseFuelLitres, 1)} L)`, testId: 'sim-new-fuel' },
                  { label: 'Ny diesel per tonn', value: `${fmtNumber(out.newLitresPerTonne, 4)} L/t (fra ${fmtNumber(out.baseLitresPerTonne, 4)} L/t)`, testId: 'sim-new-lpt' },
                  { label: 'Produksjon', value: `${fmtNumber(input.baseTonnes, 0)} t (uendret)` },
                  { label: 'Produktive timer', value: 'uendret' },
                ]}
              />
              <p className="mt-4 text-[13px] leading-5 text-[var(--hr-muted)]">Simulering med antatte satser – kun drivstoffeffekten vises som mulig besparelse.</p>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {canSave ? (
                  <Button onClick={save} disabled={!validLph} data-testid="simulate-idle-save-proposal-button">
                    <Save className="mr-1.5 h-4 w-4" aria-hidden="true" /> Lagre som tiltaksforslag
                  </Button>
                ) : (
                  <p className="text-sm text-[var(--hr-muted)]">Rollen {roleLabel(role)} kan simulere, men ikke lagre forslag. Bytt til Ledelse for å lagre.</p>
                )}
              </div>
            </div>
          </div>
        )}
        {saved.length > 0 && (
          <div data-testid="saved-simulations">
            <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Lagrede tiltaksforslag ({saved.length})</div>
            <ul className="divide-y divide-[var(--hr-hairline)] rounded-[var(--hr-radius-sm)] border border-[var(--hr-hairline)]">
              {[...saved].reverse().map((s) => (
                <li key={s.id} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3 text-sm" data-testid={`saved-simulation-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-mono text-[13px] text-[var(--hr-muted)]">{s.id}</div>
                    <div className="hr-tabular leading-6">
                      {s.inputAssumptions.reductionPct} % mindre tomgang ved {fmtNumber(s.inputAssumptions.litresPerIdleHour, 1)} L/h → {fmtNumber(s.derivedResults.fuelSavedLitres, 1)} L, {fmtNumber(s.derivedResults.nokSaved, 0)} NOK, {fmtNumber(s.derivedResults.co2SavedKg, 3)} kg CO₂; ny L/t {fmtNumber(s.derivedResults.newLitresPerTonne, 4)}
                    </div>
                    <div className="text-[13px] text-[var(--hr-muted)]">{s.savedBy} ({roleLabel(s.role)}) · {formatDateTime(s.savedAt)} · {formatRangeShort(s.basePeriod.fromDate, s.basePeriod.toDate)}</div>
                  </div>
                  {canSave && (
                    <Button variant="ghost" size="sm" className="h-8 text-[var(--hr-muted)]" onClick={() => store.removeSimulation(s.id)} aria-label={`Slett ${s.id}`} data-testid={`delete-simulation-${s.id}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Panel>
  );
}

export default function AvvikDetaljPage() {
  const { id: rawId } = useParams();
  const id = decodeURIComponent(rawId ?? '');
  const r = useResults();
  const { role, session } = useSession();
  const store = useDemoStore();
  const [comment, setComment] = useState('');
  const dev = useMemo(() => (r.ready ? r.deviations.find((d) => d.id === id) : null), [r.ready, r.deviations, id]);

  if (!r.ready) return <PageSkeleton />;
  if (!dev) {
    const parsed = parseDeviationId(id);
    const mismatch = parsed && (parsed.scenarioId !== r.scenarioId || parsed.fromDate !== r.fromDate || parsed.toDate !== r.toDate);
    return (
      <EmptyState
        type="no_results"
        title="Avviket finnes ikke for gjeldende periode og scenario"
        description={mismatch ? `Avviket tilhører scenario «${parsed.scenarioId}» og perioden ${formatRangeShort(parsed.fromDate, parsed.toDate)}. Avvik beregnes på nytt fra grunnlagsdata når filtrene endres.` : 'Avviks-ID-en ble ikke gjenkjent. Avvik beregnes deterministisk fra grunnlagsdata for valgt periode.'}
        action={
          <>
            {mismatch && (
              <Button size="sm" asChild>
                <Link to={`/avvik/${encodeURIComponent(id)}?scenario=${parsed.scenarioId}&fra=${parsed.fromDate}&til=${parsed.toDate}`} data-testid="go-to-deviation-period-button">Gå til avvikets periode</Link>
              </Button>
            )}
            <Button variant="outline" size="sm" asChild>
              <Link to={r.linkTo('/avvik')}>Til avvikslisten</Link>
            </Button>
          </>
        }
      />
    );
  }

  const status = store.statusOf(dev.id);
  const annotations = store.annotationsFor(dev.id);
  const canComment = can(role, 'comment');
  const canStatus = can(role, 'change_status');
  const showSim = dev.ruleId !== 'R3_DATA_QUALITY' || r.current.idleHours.status === 'ok';
  const simResult = dev.scope.machineId ? null : r.current;
  const machine = dev.scope.machineId ? r.dataset?.machines.find((m) => m.machineId === dev.scope.machineId) : null;
  const subject = dev.scope.machineId ? `${machineLabel(dev.scope.machineId)} · ${machine?.fictionalName ?? ''}` : dev.scope.sourceId ? `Kilde ${presentText(dev.scope.sourceId)}` : 'Linje 1 (samlet)';
  const relatedMachines = dev.ruleId === 'R1_DIESEL_PER_TONNE' ? r.deviations.filter((d) => d.ruleId === 'R2_IDLE_SHARE' && d.scope.machineId).map((d) => d.scope.machineId) : [];

  const setStatus = (s) => {
    store.setStatus({ deviationId: dev.id, status: s, demoAuthor: session.demoUser, role, now: new Date().toISOString() });
    toast.message(`Status satt til ${DEVIATION_STATUS_LABELS[s]}`, { description: 'Lagret lokalt i denne nettleseren.' });
  };
  const submitComment = (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    store.addComment({ targetId: dev.id, text: comment.trim(), demoAuthor: session.demoUser, role, now: new Date().toISOString() });
    setComment('');
    toast.success('Kommentar lagret', { description: 'Lagret lokalt i denne nettleseren.' });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: 'Avvik', to: r.linkTo('/avvik'), testId: 'back-to-deviations-link' }, { label: dev.ruleLabel }]}
        title={dev.ruleLabel}
        lead={`${subject} · ${formatRange(dev.period.fromDate, dev.period.toDate)}${dev.reference ? ` mot ${formatRange(dev.reference.fromDate, dev.reference.toDate)}` : ''} · ${presentId(dev.id)}`}
      >
        <SeverityBadge severity={dev.severity} />
        <DeviationStatusBadge status={status} />
      </PageHeader>

      <div className="grid gap-5 lg:grid-cols-12">
        <Panel className="min-w-0 lg:col-span-8" data-testid="deviation-detail-panel">
          <div className="divide-y divide-[var(--hr-hairline)]">
            <section className="grid gap-2 px-5 py-4 sm:grid-cols-[140px_1fr] sm:gap-4">
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)] sm:pt-1">Observasjon</h3>
              <div>
                <p className="hr-tabular text-[16px] leading-7 text-[var(--hr-text)]" data-testid="deviation-observation">{presentText(dev.observation)}</p>
                <p className="hr-tabular mt-1 text-sm text-[var(--hr-muted)]">Observert: {presentText(dev.observed)}</p>
              </div>
            </section>
            <section className="grid gap-2 px-5 py-4 sm:grid-cols-[140px_1fr] sm:gap-4">
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-amber)] sm:pt-1">Mulig forklaring</h3>
              <p className="text-[16px] leading-7" data-testid="deviation-explanation">{presentText(dev.possibleExplanation)}</p>
            </section>
            <section className="grid gap-2 px-5 py-4 sm:grid-cols-[140px_1fr] sm:gap-4">
              <h3 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)] sm:pt-1">Neste kontroll</h3>
              <div>
                <p className="text-[16px] leading-7" data-testid="deviation-next-check">{presentText(dev.nextCheck)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {dev.scope.machineId && (
                    <Button asChild size="sm" variant="outline">
                      <Link to={r.linkTo(`/maskiner/${dev.scope.machineId}`, { vis: 'tomgang' })} data-testid="deviation-open-machine-button">
                        Åpne {machineLabel(dev.scope.machineId)} <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                      </Link>
                    </Button>
                  )}
                  {dev.ruleId === 'R1_DIESEL_PER_TONNE' && (
                    <>
                      <Button asChild size="sm" variant="outline">
                        <Link to={r.linkTo('/oversikt', { forklar: 'dieselPerTonne' })} data-testid="deviation-open-kpi-button">Vis beregningen av L/t</Link>
                      </Button>
                      {relatedMachines.map((m) => (
                        <Button key={m} asChild size="sm" variant="outline">
                          <Link to={r.linkTo(`/maskiner/${m}`, { vis: 'tomgang' })} data-testid={`deviation-open-${m}`}>Gå til {machineLabel(m)}</Link>
                        </Button>
                      ))}
                    </>
                  )}
                  {dev.scope.sourceId && (
                    <Button asChild size="sm" variant="outline">
                      <Link to={r.linkTo('/datakilder', dev.evidenceRecordIds[0] ? { post: dev.evidenceRecordIds[0] } : {})} data-testid="deviation-open-source-button">Åpne Datakilder</Link>
                    </Button>
                  )}
                </div>
              </div>
            </section>
            <section className="px-5 py-4">
              <ProvenanceList recordIds={dev.evidenceRecordIds} label="Kildebelegg" testId="deviation-evidence" />
            </section>
          </div>
        </Panel>

        <Panel className="min-w-0 self-start lg:col-span-4" data-testid="deviation-actions-panel">
          <PanelHeader title="Status og kommentarer" />
          <div className="space-y-5 px-5 py-4">
            <div>
              <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Status</div>
              {canStatus ? (
                <div className="inline-flex flex-wrap rounded-[10px] border border-[var(--hr-border)] bg-white p-0.5" role="group" aria-label="Sett status">
                  {['open', 'reviewing', 'resolved'].map((s) => (
                    <Button key={s} variant={status === s ? 'default' : 'ghost'} size="sm" className="h-8 rounded-[8px] text-[13px]" onClick={() => setStatus(s)} aria-pressed={status === s} data-testid={`set-status-${s}`}>
                      {DEVIATION_STATUS_LABELS[s]}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <DeviationStatusBadge status={status} />
                  <span className="text-[var(--hr-muted)]">Kun rollen Produksjon kan endre status.</span>
                </div>
              )}
            </div>
            <div>
              <div className="mb-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Historikk</div>
              {annotations.length === 0 && <p className="text-sm text-[var(--hr-muted)]">Ingen hendelser ennå.</p>}
              <ul className="space-y-3">
                {[...annotations].reverse().map((a) => (
                  <li key={a.id} className="flex gap-2.5 text-sm" data-testid={`annotation-${a.kind}-${a.id}`}>
                    {a.kind === 'comment' ? <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-[var(--hr-muted)]" aria-hidden="true" /> : <History className="mt-0.5 h-4 w-4 shrink-0 text-[var(--hr-muted)]" aria-hidden="true" />}
                    <div>
                      <div className="text-[13px] text-[var(--hr-muted)]">{a.demoAuthor} ({roleLabel(a.role)}) · {formatDateTime(a.createdAt)}</div>
                      <div className="leading-6">{a.kind === 'comment' ? a.text : `Status endret fra ${DEVIATION_STATUS_LABELS[a.fromStatus]} til ${DEVIATION_STATUS_LABELS[a.toStatus]}`}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {canComment ? (
              <form onSubmit={submitComment} className="space-y-2">
                <Label htmlFor="dev-comment" className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Kommentar</Label>
                <Textarea id="dev-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Skriv en kommentar …" className="bg-white" data-testid="deviation-comment-input" />
                <Button type="submit" size="sm" disabled={!comment.trim()} data-testid="deviation-comment-submit">Lagre kommentar</Button>
              </form>
            ) : (
              <p className="text-sm text-[var(--hr-muted)]">Rollen {roleLabel(role)} kan ikke legge inn kommentarer.</p>
            )}
          </div>
        </Panel>
      </div>

      {showSim && <SimulationPanel result={simResult ?? r.current} deviationId={dev.id} scenario={r.scenario} />}
    </div>
  );
}
